import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Sparkles, Trophy, Tv, PartyPopper, Check, Rocket, Handshake, Globe, Lightbulb, Award } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import { BRAND } from "@/lib/api";

export default function WhoWeAre() {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const goFor = t("whoweare.go_for", { returnObjects: true }) || [];
  const P = process.env.PUBLIC_URL || "";
  const IMG = {
    carnival: `${P}/whoweare/carnival.jpg`,
    sony: `${P}/whoweare/sony.jpg`,
    aperol: `${P}/whoweare/aperol.jpg`,
  };
  const wcPhotos = Array.from({ length: 15 }, (_, i) => `${P}/whoweare/worldclass/wc${String(i + 1).padStart(2, "0")}.jpg`);
  const LEO = "https://customer-assets-eiarnc6j.emergentagent.net/job_beverage-tech-1/artifacts/a6l1u34l_leo02.webp";
  const leoMilestones = [
    { key: "m1", Icon: Lightbulb },
    { key: "m2", Icon: Award },
    { key: "m3", Icon: Rocket },
  ];
  const [wcApi, setWcApi] = useState(null);

  useEffect(() => {
    if (!wcApi) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const root = wcApi.rootNode();
    let paused = false;
    const enter = () => { paused = true; };
    const leave = () => { paused = false; };
    root.addEventListener("mouseenter", enter);
    root.addEventListener("focusin", enter);
    root.addEventListener("mouseleave", leave);
    root.addEventListener("focusout", leave);
    const id = setInterval(() => { if (!paused && document.visibilityState === "visible") wcApi.scrollNext(); }, 3800);
    return () => {
      clearInterval(id);
      root.removeEventListener("mouseenter", enter);
      root.removeEventListener("focusin", enter);
      root.removeEventListener("mouseleave", leave);
      root.removeEventListener("focusout", leave);
    };
  }, [wcApi]);

  const reveal = reduce
    ? {}
    : {
        initial: { opacity: 0, y: 26 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-70px" },
        transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
      };

  const Eyebrow = ({ children }) => (
    <p className="preshh-eyebrow text-[#00B5D8]">{children}</p>
  );

  const timeline = [
    { year: t("whoweare.t2012_year"), title: t("whoweare.t2012_title"), body: t("whoweare.t2012_body"), Icon: Rocket },
    { year: t("whoweare.t2018_year"), title: t("whoweare.t2018_title"), body: t("whoweare.t2018_body"), Icon: Handshake },
    { year: t("whoweare.scale_year"), title: t("whoweare.scale_title"), body: t("whoweare.scale_body"), Icon: Globe },
  ];

  return (
    <div className="text-white" data-testid="whoweare-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10" aria-labelledby="whoweare-title">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 15% 20%, #00B5D8 0, transparent 42%), radial-gradient(circle at 90% 10%, #00B5D8 0, transparent 38%)" }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid lg:grid-cols-[1.05fr_0.95fr] gap-14 items-center">
          <motion.div {...reveal}>
            <Eyebrow>{t("whoweare.eyebrow")}</Eyebrow>
            <h1 id="whoweare-title" data-testid="whoweare-title" className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
              {t("whoweare.title")}
            </h1>
            <p className="mt-6 max-w-xl text-base md:text-lg text-white/70 leading-relaxed">
              {t("whoweare.intro")}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/preshhgo" data-testid="whoweare-hero-cta"
                className="preshh-btn-primary text-sm inline-flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00B5D8] focus-visible:ring-offset-2 focus-visible:ring-offset-black">
                {t("whoweare.cta_button")} <ArrowRight size={16} aria-hidden="true" />
              </Link>
              <Link to="/how-it-works" data-testid="whoweare-hero-secondary"
                className="preshh-btn-ghost text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00B5D8] focus-visible:ring-offset-2 focus-visible:ring-offset-black">
                {t("nav.how_it_works")}
              </Link>
            </div>
          </motion.div>
          <motion.div {...(reduce ? {} : { ...reveal, transition: { ...reveal.transition, delay: 0.1 } })}
            className="relative">
            <figure className="m-0">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-2xl">
                <img src={BRAND.bartenderSplash} alt="The Vogal cocktail, carbonated with Preshh, served at Marlindo 1959"
                  className="w-full h-full object-cover" loading="eager" />
              </div>
              <figcaption data-testid="whoweare-hero-credit" className="mt-3 text-xs text-white/50 tracking-wide">
                <span className="text-white/75 font-medium">Vogal</span> — Marlindo 1959
              </figcaption>
            </figure>
          </motion.div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="border-b border-white/10" aria-labelledby="philosophy-title">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div {...reveal} className="order-2 lg:order-1">
            <div className="aspect-[16/11] rounded-3xl overflow-hidden ring-1 ring-white/10">
              <img src={BRAND.bartenderCocktail} alt="A bartender crafting a drink behind a bar with the Preshh system"
                className="w-full h-full object-cover" loading="lazy" />
            </div>
          </motion.div>
          <motion.div {...reveal} className="order-1 lg:order-2">
            <Eyebrow>{t("whoweare.philosophy_eyebrow")}</Eyebrow>
            <h2 id="philosophy-title" className="mt-4 font-display text-3xl md:text-4xl leading-tight">
              {t("whoweare.philosophy_title")}
            </h2>
            <p className="mt-5 text-base md:text-lg text-white/70 leading-relaxed">
              {t("whoweare.philosophy_body")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* STORY / TIMELINE */}
      <section className="border-b border-white/10" aria-labelledby="story-title">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <motion.div {...reveal} className="max-w-2xl">
            <Eyebrow>{t("whoweare.story_eyebrow")}</Eyebrow>
            <h2 id="story-title" className="mt-4 font-display text-3xl md:text-4xl">{t("whoweare.story_title")}</h2>
          </motion.div>
          <div className="mt-16 relative">
            {/* Playful connectors */}
            <div aria-hidden="true" className="md:hidden absolute left-7 top-6 bottom-6 w-px bg-gradient-to-b from-[#00B5D8]/60 via-[#00B5D8]/40 to-transparent" />
            <div aria-hidden="true" className="hidden md:block absolute top-9 left-[16.66%] right-[16.66%] border-t-2 border-dashed border-[#00B5D8]/35" />
            <ol className="grid gap-12 md:grid-cols-3 md:gap-8">
              {timeline.map((item, i) => (
                <motion.li key={i} {...(reduce ? {} : { ...reveal, transition: { ...reveal.transition, delay: i * 0.12 } })}
                  className="relative flex items-start gap-5 md:flex-col md:items-center md:text-center md:gap-0">
                  <div className="relative z-10 flex-shrink-0">
                    <span className="tl-node flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-[#00B5D8] to-[#0089a6] text-black shadow-lg shadow-[#00B5D8]/25 ring-4 ring-[#00B5D8]/15" style={{ animationDelay: `${i * 0.5}s` }}>
                      <item.Icon size={26} strokeWidth={2} aria-hidden="true" />
                    </span>
                  </div>
                  <div className="md:mt-6 md:max-w-xs">
                    <span className="font-display text-3xl md:text-4xl text-[#00B5D8]">{item.year}</span>
                    <h3 className="mt-1.5 text-lg font-bold">{item.title}</h3>
                    <p className="mt-2 text-sm md:text-[15px] text-white/70 leading-relaxed">{item.body}</p>
                  </div>
                </motion.li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* LEOPOLDO — THE INVENTOR */}
      <section className="border-b border-white/10" aria-labelledby="leo-title">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24 grid lg:grid-cols-[0.82fr_1.18fr] gap-12 lg:gap-16 items-start">
          <motion.figure {...reveal} className="m-0 lg:sticky lg:top-28">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-2xl">
              <img src={LEO} alt="Leopoldo Almeida, inventor and founder of Preshh, smiling behind a bar" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <figcaption className="mt-3 text-xs text-white/50">{t("whoweare.leo_caption")}</figcaption>
          </motion.figure>
          <motion.div {...reveal}>
            <p className="preshh-eyebrow text-[#00B5D8]">{t("whoweare.leo_eyebrow")}</p>
            <h2 id="leo-title" className="mt-4 font-display text-3xl md:text-4xl leading-tight">{t("whoweare.leo_title")}</h2>
            <p className="mt-2 text-sm font-semibold text-[#00B5D8]/90">{t("whoweare.leo_role")}</p>
            <p className="mt-5 text-base md:text-lg text-white/75 leading-relaxed">{t("whoweare.leo_intro")}</p>
            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              {leoMilestones.map(({ key, Icon }) => (
                <div key={key} className="rounded-2xl bg-[#111] border border-white/10 p-5">
                  <div className="w-9 h-9 rounded-xl bg-[#00B5D8]/12 flex items-center justify-center mb-3">
                    <Icon size={18} className="text-[#00B5D8]" aria-hidden="true" />
                  </div>
                  <p className="font-bold text-sm">{t(`whoweare.leo_${key}_t`)}</p>
                  <p className="mt-1.5 text-[13px] text-white/65 leading-relaxed">{t(`whoweare.leo_${key}_b`)}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm md:text-[15px] text-white/70 leading-relaxed">{t("whoweare.leo_bg")}</p>
            <p className="mt-3 text-sm md:text-[15px] text-white/70 leading-relaxed">{t("whoweare.leo_shark")}</p>
            <blockquote className="mt-8 border-l-2 border-[#00B5D8] pl-5">
              <p className="font-display text-xl md:text-2xl leading-snug">“{t("whoweare.leo_quote")}”</p>
              <p className="mt-3 text-sm text-white/60 leading-relaxed">{t("whoweare.leo_manifesto")}</p>
            </blockquote>
          </motion.div>
        </div>
      </section>

      {/* PROOF OF EXPERIENCE */}
      <section className="border-b border-white/10" aria-labelledby="proof-title">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <motion.div {...reveal} className="max-w-2xl">
            <Eyebrow>{t("whoweare.proof_eyebrow")}</Eyebrow>
            <h2 id="proof-title" className="mt-4 font-display text-3xl md:text-4xl">{t("whoweare.proof_title")}</h2>
          </motion.div>

          {/* World Class */}
          <motion.div {...reveal} className="mt-12 grid lg:grid-cols-2 gap-10 items-center">
            <Carousel opts={{ align: "start", loop: true }} setApi={setWcApi} className="w-full" data-testid="wc-carousel">
              <CarouselContent>
                {wcPhotos.map((src, i) => (
                  <CarouselItem key={i}>
                    <div className="aspect-square rounded-3xl overflow-hidden ring-1 ring-white/10">
                      <img src={src} alt={`A World Class competitor preparing a carbonated cocktail with Preshh (${i + 1} of ${wcPhotos.length})`}
                        className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious data-testid="wc-prev" className="left-3 h-10 w-10 bg-black/50 border-white/20 text-white hover:bg-[#00B5D8] hover:text-black hover:border-[#00B5D8]" />
              <CarouselNext data-testid="wc-next" className="right-3 h-10 w-10 bg-black/50 border-white/20 text-white hover:bg-[#00B5D8] hover:text-black hover:border-[#00B5D8]" />
            </Carousel>
            <div>
              <div className="inline-flex items-center gap-2 text-[#00B5D8] text-sm font-semibold">
                <Trophy size={18} aria-hidden="true" /> {t("whoweare.wc_title")}
              </div>
              <p className="mt-4 text-base md:text-lg text-white/75 leading-relaxed">{t("whoweare.wc_body")}</p>
            </div>
          </motion.div>

          {/* Purgatório — stats + real image */}
          <motion.div {...reveal}
            className="mt-8 rounded-3xl bg-gradient-to-br from-[#06222a] to-[#0a0a0a] ring-1 ring-[#00B5D8]/25 overflow-hidden grid lg:grid-cols-2">
            <div className="min-h-[240px] lg:min-h-full">
              <img src={IMG.carnival} alt="A vividly carbonated drink prepared with the Preshh system during Carnival"
                className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="p-8 md:p-12">
              <div className="inline-flex items-center gap-2 text-[#00B5D8] text-sm font-semibold">
                <PartyPopper size={18} aria-hidden="true" /> {t("whoweare.purg_title")}
              </div>
              <p className="mt-4 text-base md:text-lg text-white/75 leading-relaxed">{t("whoweare.purg_body")}</p>
              <dl className="mt-8 grid sm:grid-cols-2 gap-4">
                <div className="rounded-2xl bg-black/30 border border-white/10 p-6">
                  <dd className="font-display text-4xl md:text-5xl text-white">{t("whoweare.stat_week_num")}</dd>
                  <dt className="mt-2 text-sm text-white/60">{t("whoweare.stat_week_label")}</dt>
                </div>
                <div className="rounded-2xl bg-black/30 border border-white/10 p-6">
                  <dd className="font-display text-4xl md:text-5xl text-white">{t("whoweare.stat_day_num")}</dd>
                  <dt className="mt-2 text-sm text-white/60">{t("whoweare.stat_day_label")}</dt>
                </div>
              </dl>
            </div>
          </motion.div>

          {/* Sony */}
          <motion.div {...reveal} className="mt-8 grid lg:grid-cols-2 gap-10 items-center rounded-3xl bg-[#111] border border-white/10 overflow-hidden">
            <div className="p-8 md:p-12 order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 text-[#00B5D8] text-sm font-semibold">
                <Tv size={18} aria-hidden="true" /> {t("whoweare.sony_kicker")}
              </div>
              <h3 className="mt-4 font-display text-2xl md:text-3xl leading-tight">{t("whoweare.sony_title")}</h3>
              <p className="mt-4 text-base text-white/70 leading-relaxed">{t("whoweare.sony_body")}</p>
            </div>
            <div className="aspect-[16/10] lg:aspect-auto lg:h-full order-1 lg:order-2">
              <img src={IMG.sony} alt="Judges and a host tasting Preshh-carbonated drinks on the Shaking the Bar reality show"
                className="w-full h-full object-cover" loading="lazy" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* PRESHH GO */}
      <section className="border-b border-white/10" aria-labelledby="go-title">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div {...reveal}>
            <Eyebrow>{t("whoweare.go_eyebrow")}</Eyebrow>
            <h2 id="go-title" className="mt-4 font-display text-3xl md:text-4xl leading-tight">{t("whoweare.go_title")}</h2>
            <p className="mt-5 text-base md:text-lg text-white/70 leading-relaxed">{t("whoweare.go_body")}</p>

            <p className="mt-8 text-sm font-semibold text-white/90">{t("whoweare.go_for_label")}</p>
            <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {goFor.map((it, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-white/75">
                  <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-[#00B5D8]/15 flex items-center justify-center">
                    <Check size={12} className="text-[#00B5D8]" strokeWidth={3} aria-hidden="true" />
                  </span>
                  {it}
                </li>
              ))}
            </ul>

            <div className="mt-8 flex items-start gap-3 rounded-2xl bg-[#00B5D8]/8 border border-[#00B5D8]/20 p-5">
              <Sparkles size={18} className="text-[#00B5D8] flex-shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm md:text-[15px] text-white/85 leading-relaxed">{t("whoweare.go_example")}</p>
            </div>
          </motion.div>
          <motion.div {...reveal}>
            <div className="aspect-[4/5] rounded-3xl overflow-hidden ring-1 ring-white/10">
              <img src={IMG.aperol} alt="A perfectly carbonated Aperol Spritz made with Preshh GO"
                className="w-full h-full object-cover" loading="lazy" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden" aria-labelledby="cta-title">
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 50% 30%, #00B5D8 0, transparent 55%)" }} />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 py-24 md:py-28 text-center">
          <motion.div {...reveal}>
            <h2 id="cta-title" className="font-display text-3xl sm:text-4xl md:text-5xl leading-tight">{t("whoweare.cta_title")}</h2>
            <p className="mt-5 text-base md:text-lg text-white/70 max-w-2xl mx-auto">{t("whoweare.cta_body")}</p>
            <div className="mt-9">
              <Link to="/preshhgo" data-testid="whoweare-final-cta"
                className="preshh-btn-primary text-base inline-flex items-center gap-2 px-8 py-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00B5D8] focus-visible:ring-offset-2 focus-visible:ring-offset-black">
                {t("whoweare.cta_button")} <ArrowRight size={18} aria-hidden="true" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
